import { BasicFieldReference } from "../models/BasicField";
import ContactField from "../models/ContactField";
import { OrderField } from "../models/OrderField";
import { FieldMapper } from "s2-field-mapper/build.npm";
import { retry } from "./async";
import { configBase } from "./theme";
import { getAccessToken } from "./user";
import { isGenericNetworkError } from "../backend";

type _Window = Window & { _fieldMapper?: FieldMapper };
const empty = new FieldMapper('');

export function getFieldMapper(safe?: boolean) {
  const mapper = (window as _Window)._fieldMapper;

  if (!mapper && safe) {
    return empty;
  }

  return mapper;
}

if (getAccessToken()) {
  const fieldMapperSchemaCache = localStorage.getItem('fieldMapperSchema');
  if (fieldMapperSchemaCache) {
    try {
      (window as _Window)._fieldMapper = new FieldMapper(fieldMapperSchemaCache);
    } catch (error) {
      console.error("Error creating field mapper from cache", error);
    }
  }

  //  Pre-load field mapper.
  retry(() => fetch(new URL("/field-mapper-schema", configBase).href, {
    cache: "no-cache"
  })
    .then(response => {
      return response.text().then(text => {
        const type = response.headers.get('content-type');

        if (type.includes('text/html')) {
          throw new Error("Received HTML");
        }

        if (response.status !== 200) {
          throw new Error(response.status + ' ' + text);
        }

        (window as _Window)._fieldMapper = new FieldMapper(text);
        localStorage.setItem('fieldMapperSchema', text);
      })
    }))
    .catch(error => {
      if (!isGenericNetworkError(error)) {
        console.error("alert: Error pre-loading field mapper", error);
      }
    });
}

export function listMappedOrderFieldReferences(
  orderFields: OrderField[],
  contactFields: ContactField[],
  contactFieldReferences: BasicFieldReference[]
): BasicFieldReference[] {
  if (!contactFieldReferences?.length) {
    return [];
  }

  const joined = listJoinedOrderFieldReferences(orderFields, contactFieldReferences);
  const split = listSplitOrderFieldReferences(contactFields, contactFieldReferences);

  const valueByFieldId: { [fieldId: string]: string } = {};

  joined.forEach(({ orderFieldId, value }) => valueByFieldId[orderFieldId] = value);
  split.forEach(({ orderFieldId, value }) => valueByFieldId[orderFieldId] = value);

  const references = Object.entries(valueByFieldId).map(([fieldId, value]) => ({ fieldId, value }));

  if (getFieldMapper()) {
    //  List of field IDs already handled and are more specific than the default mapper.
    const ignoreFieldIds = [
      ...joined.map(({ contactFieldIds }) => contactFieldIds),
      ...split.map(({ contactFieldIds }) => contactFieldIds)
    ].flat();

    const result = getFieldMapper().map(contactFields,
      orderFields,
      contactFieldReferences.filter(({ fieldId }) => !ignoreFieldIds.includes(fieldId))
    );

    result.forEach(element => {
      references.push(element);
    });
  }

  return references;
}

type MappedFieldReference = {
  contactFieldIds: string[];
  orderFieldId: string;
  value: string;
};

/**
 * Returns a list of order field references given a list of order fields, and contact field references.
 * Used when creating an offer or order from a contact.
 */
export function listJoinedOrderFieldReferences(orderFields: OrderField[],
  contactFieldReferences: BasicFieldReference[]): MappedFieldReference[] {

  let mappedReferences: MappedFieldReference[] = [];

  const cfValueById = contactFieldReferences.reduce((object, field) => {
    object[field.fieldId] = field.value;
    return object;
  }, {} as { [fieldId: string]: string });

  for (const orderField of orderFields) {
    for (const group of orderField.contactFields) {
      //  Retain order from group.contactFieldIds
      const values = group.contactFieldIds.map(fieldId => cfValueById[fieldId])
        .filter(string => string);

      mappedReferences.push({
        contactFieldIds: group.contactFieldIds,
        orderFieldId: orderField.id,
        value: values.join(' ')
      });
    }
  }

  return mappedReferences.filter(({ value }) => value);
}

/**
 * Returns a list of order field references given a list of contact fields and contact field references.
 * Used when creating an offer or order from a contact.
 */
export function listSplitOrderFieldReferences(contactFields: ContactField[], contactFieldReferences: BasicFieldReference[]):
  MappedFieldReference[] {
  let mappedReferences: MappedFieldReference[] = [];

  contactFieldReferences.forEach(({ fieldId: contactFieldId, value }) => {
    const contactField = contactFields.find(field => field.id === contactFieldId);
    if (!contactField?.orderFields?.length) {
      return;
    }

    contactField.orderFields.forEach(({ orderFieldIds, pattern }) => {
      //  Splits by string. Substrings encapsulated in double or single quotes will not be split.
      const defaultRegexp = /([^\s"']+)|"([^"]*)"|'([^']*)'/igmu;
      const regexp = compilePattern(pattern) || defaultRegexp;

      const parts = listMatchedSubstrings(value, regexp);

      orderFieldIds.forEach((orderFieldId, index) => {
        if (index === orderFieldIds.length - 1) {
          //  Join all remaining parts for the last order field reference.
          mappedReferences.push({
            contactFieldIds: [contactFieldId],
            orderFieldId,
            value: parts.slice(index).join(' ')
          });
        } else if (typeof parts[index] === 'string') {
          mappedReferences.push({
            contactFieldIds: [contactFieldId],
            orderFieldId,
            value: parts[index]
          });
        }
      });
    });
  });

  return mappedReferences.filter(({ value }) => value);
}

/**
 * Returns all `regexp` capture groups from `string`.
 */
function listMatchedSubstrings(string: string, regexp: RegExp) {
  const matches: string[] = [];

  while (true) {
    const result = regexp.exec(string);
    if (!result?.length) {
      break;
    }

    for (let i = 1; i < result.length; i++) {
      if (result[i]) {
        matches.push(result[i]);
      }
    }
  }

  return matches;
}

function compilePattern(pattern: string): RegExp | null {
  if (!pattern) {
    return null;
  }

  try {
    const [_0, patternWithinBounds, flags] = /^\/(.+)\/([\w]+)?$/u.exec(pattern) || [];

    //  Enforce standard flags for listMatchedSubstrings to work.
    return new RegExp(patternWithinBounds || pattern, 'igmu');
  } catch (error) {
    console.error("Failed to compile pattern: %s", pattern, error);
    return null;
  }
}

/**
 * Removes fieldId duplicates.
 */
export function removeFieldReferenceDuplicates<T extends BasicFieldReference>(references: T[]): T[] {
  if (!Array.isArray(references)) {
    return references;
  }
  
  const byId = references.reduce((object, reference) => {
    const existing = object[reference.fieldId];

    //  Referensen sist i listan gäller.
    if (!existing || reference.value) {
      object[reference.fieldId] = reference;
    }

    return object;
  }, {} as ById<T>)

  return Object.values(byId);
}